import { createAsyncThunk, createEntityAdapter, createSlice } from '@reduxjs/toolkit';
import { availablePageSizes } from 'config';
import { PageResponse } from '@payer-admin-ui/api/src/utils/repository/PageResponse';
import { createPaginationAdapter } from '@payer-admin-ui/components-v2/src/state/paginationAdapter';
import { AppEntityState, FetchingEntityState } from '@payer-admin-ui/components-v2/src/types/state';
import { paymentTerminalsAPI, PaymentTerminal } from '../../../redux/paymentTerminalsAPI';
import { MerchantAdminState } from '../../../redux/store';
import { clearTableSettings } from '../../../redux/userSettings';

export const PAYMENT_TERMINALS_TABLE_SETTINGS_KEY = 'paymentTerminals';

export type PaymentTerminalsGlobalState = AppEntityState<PaymentTerminal, string> & {
  listView: {
    inited: boolean;
    isFetching: boolean;
    error: any;
    ids: string[];
    page: number;
    pageSize: number;
    totalCount: number | null;
  };
};

export type PaymentTerminalEntity = FetchingEntityState<PaymentTerminal, string>;

const entityAdapter = createEntityAdapter<PaymentTerminalEntity>();

const paginationAdapter = createPaginationAdapter<PaymentTerminalsGlobalState>({
  selectState: state => state.listView,
});

const initialState: PaymentTerminalsGlobalState = entityAdapter.getInitialState({
  listView: {
    inited: false,
    isFetching: false,
    error: null,
    ids: [],
    page: 0,
    pageSize: availablePageSizes[0],
    totalCount: null,
  },
});

export const fetchPaymentTerminalsPage = createAsyncThunk<
  PageResponse<PaymentTerminal>,
  void,
  { state: MerchantAdminState }
>('paymentTerminals/fetchPage', async (_, { getState }) => {
  const { page, pageSize } = getState().paymentTerminals.global.listView;
  const res = await paymentTerminalsAPI.fetchPage({ page, pageSize });
  return res;
});

export const syncUserSettings = createAsyncThunk<void, void, { state: MerchantAdminState }>(
  'paymentTerminals/syncUserSettings',
  async (_, { getState, dispatch }) => {
    const settings = getState().userSettings?.tableSettings?.[PAYMENT_TERMINALS_TABLE_SETTINGS_KEY];
    const pageSize = settings?.pageSize;
    if (pageSize) dispatch(setPageSize(pageSize));
    dispatch(setInited());
  },
);

export const clearPaymentTerminalsTableSettings = createAsyncThunk<void, void, { state: MerchantAdminState }>(
  'paymentTerminals/clearTableSettings',
  async (_, { dispatch }) => {
    dispatch(setPage(0));
    dispatch(setPageSize(availablePageSizes[0]));
    dispatch(
      clearTableSettings({
        component: PAYMENT_TERMINALS_TABLE_SETTINGS_KEY,
      }),
    );
  },
);

const paymentTerminalsSlice = createSlice({
  name: 'paymentTerminals/global',
  initialState,
  reducers: {
    setInited: state => {
      state.listView.inited = true;
    },
    resetError: state => {
      state.listView.error = null;
    },
    setPage: paginationAdapter.setPage,
    setPageSize: paginationAdapter.setPageSize,
  },
  extraReducers: builder => {
    builder.addCase(fetchPaymentTerminalsPage.pending, state => {
      state.listView.isFetching = true;
      state.listView.error = null;
    });
    builder.addCase(fetchPaymentTerminalsPage.fulfilled, (state, { payload: { contents, totalCount } }) => {
      const rows = contents.map(item => ({ id: item.id, entity: item, isFetching: false, error: null }));
      state.listView.ids = rows.map(r => r.id);
      state.listView.totalCount = totalCount;
      state.listView.isFetching = false;
      entityAdapter.upsertMany(state, rows);
    });
    builder.addCase(fetchPaymentTerminalsPage.rejected, (state, { error }) => {
      state.listView.ids = [];
      state.listView.totalCount = null;
      state.listView.isFetching = false;
      state.listView.error = error;
    });
  },
});

export const {
  actions: { setInited, resetError, setPage, setPageSize },
  reducer: paymentTerminalsReducer,
} = paymentTerminalsSlice;

export const paymentTerminalSelectors = entityAdapter.getSelectors<MerchantAdminState>(
  state => state.paymentTerminals.global,
);
